import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Star, Trash2, LogIn, User } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toNeDigits } from "@/lib/constants";
import { REVIEWS } from "@/constants/testIds";
import { toast } from "sonner";

export const StarRating = ({ value = 0, size = "w-4 h-4", onChange, testId }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= Math.round(value);
      const icon = <Star className={`${size} ${filled ? "fill-[#F5A623] text-[#F5A623]" : "text-[#E5E7EB]"}`} />;
      return onChange ? (
        <button key={n} type="button" data-testid={testId ? `${testId}-${n}` : undefined} onClick={() => onChange(n)} className="p-0.5">
          {icon}
        </button>
      ) : (
        <span key={n}>{icon}</span>
      );
    })}
  </div>
);

export const RatingSummary = ({ average, count, testId }) => {
  const { t, lang } = useLang();
  return (
    <div className="flex items-center gap-1.5 text-sm text-[#6B7280]" data-testid={testId}>
      <StarRating value={average} />
      <span className="font-semibold text-[#14251F]">{toNeDigits(average || 0, lang)}</span>
      <span>({toNeDigits(count || 0, lang)} {t("reviews.count")})</span>
    </div>
  );
};

// Small "★★★★☆ 4.2 (7)" line for product cards.
export const CompactRating = ({ average = 0, count = 0, testId }) => {
  const { lang } = useLang();
  return (
    <div className="flex items-center gap-1 text-xs text-[#6B7280]" data-testid={testId}>
      <StarRating value={average} size="w-3.5 h-3.5" />
      {count > 0 && <span className="font-semibold text-[#14251F]">{toNeDigits(average, lang)}</span>}
      <span>({toNeDigits(count, lang)})</span>
    </div>
  );
};

// targetType: "product" | "user"; ownerId: the user who can't review this target.
// reviewerRoles: if set, only these roles see the review form.
export const Reviews = ({ targetType, targetId, ownerId, title, onChange, reviewerRoles }) => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const location = useLocation();
  const [data, setData] = useState({ average: 0, count: 0, reviews: [] });
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/reviews", { params: { target_type: targetType, target_id: targetId } }).then((res) => {
      setData(res.data);
      onChange && onChange(res.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, targetId]);

  useEffect(() => { load(); }, [load]);

  const mine = user && data.reviews.find((r) => r.author_id === user.id);

  useEffect(() => {
    setRating(mine ? mine.rating : 0);
    setComment(mine ? mine.comment : "");
  }, [mine?.id, mine?.rating, mine?.comment]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error(t("reviews.selectRating"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/reviews", { target_type: targetType, target_id: targetId, rating, comment });
      toast.success(t("toast.reviewSaved"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/reviews/${id}`);
    toast.success(t("toast.reviewDeleted"));
    load();
  };

  const canReview = user && user.id !== ownerId && (!reviewerRoles || reviewerRoles.includes(user.role));

  return (
    <div className="mt-8" data-testid={REVIEWS.root}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="text-lg font-bold text-[#14251F]">{title || t("reviews.title")}</h2>
        <RatingSummary average={data.average} count={data.count} testId={REVIEWS.summary} />
      </div>

      {!user ? (
        <Link
          to={`/login?redirect=${location.pathname}`}
          data-testid={REVIEWS.loginLink}
          className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white border border-[#E5E7EB] text-[#123A2C] font-semibold text-sm"
        >
          <LogIn className="w-4 h-4" /> {t("reviews.loginToReview")}
        </Link>
      ) : canReview ? (
        <form onSubmit={submit} data-testid={REVIEWS.form} className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
          <div className="text-sm font-medium text-[#14251F] mb-1">{mine ? t("reviews.yourReview") : t("reviews.yourRating")}</div>
          <StarRating value={rating} size="w-7 h-7" onChange={setRating} testId={REVIEWS.starInput} />
          <textarea
            data-testid={REVIEWS.commentInput}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={1000}
            rows={3}
            placeholder={t("reviews.commentPlaceholder")}
            className="mt-3 w-full rounded-xl border border-[#E5E7EB] p-3 text-sm text-[#14251F] focus:outline-none focus:border-[#123A2C]"
          />
          <button
            type="submit"
            data-testid={REVIEWS.submitButton}
            disabled={saving}
            className="mt-3 w-full py-3 rounded-xl bg-[#123A2C] text-white font-semibold hover:bg-[#0D2C21] transition-colors disabled:opacity-60"
          >
            {mine ? t("reviews.update") : t("reviews.submit")}
          </button>
        </form>
      ) : null}

      {data.reviews.length === 0 ? (
        <p className="text-sm text-[#6B7280] mt-4" data-testid={REVIEWS.empty}>{t("reviews.noReviews")}</p>
      ) : (
        <div className="space-y-3 mt-4">
          {data.reviews.map((r) => (
            <div key={r.id} data-testid={`${REVIEWS.item}-${r.id}`} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
              <div className="flex items-start justify-between gap-3">
                <Link to={`/user/${r.author_id}`} className="flex items-center gap-2 group">
                  <div className="w-8 h-8 rounded-full bg-[#DCE7E2] flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-[#123A2C]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#14251F] group-hover:text-[#123A2C]">{r.author_name}</div>
                    <div className="text-xs text-[#9A9E97]">
                      {toNeDigits(new Date(r.updated_at || r.created_at).toLocaleDateString(lang === "ne" ? "ne-NP" : "en-GB"), lang)}
                    </div>
                  </div>
                </Link>
                <div className="flex items-center gap-1">
                  <StarRating value={r.rating} />
                  {user && (r.author_id === user.id || user.role === "admin") && (
                    <button
                      data-testid={`${REVIEWS.deleteButton}-${r.id}`}
                      onClick={() => remove(r.id)}
                      aria-label={t("reviews.delete")}
                      className="p-1.5 rounded-lg text-[#DC2626] hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {r.comment && <p className="text-sm text-[#14251F] mt-2 whitespace-pre-wrap">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
